import { useState, useEffect, useRef, useMemo } from 'react'
import {
  Camera,
  Mail,
  Phone,
  User,
  LogOut,
  Save,
  Loader2,
  CheckCircle,
  GraduationCap,
  Globe,
  Edit3,
  Sun,
  Moon,
  Bell,
  BellOff,
  Trash2,
  Clock,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useEtudiantByUserId, useUpdateEtudiant } from '@/features/etudiants/hooks/use-etudiants'
import { useAgendaComplet } from '@/features/etudiants/hooks/use-agenda'
import { useDarkMode } from '@/shared/hooks/use-dark-mode'
import { isPushSupported, isPushSubscribed, subscribeToPush, unsubscribeFromPush } from '@/shared/services/push-notifications'
import { BottomNav } from '../components/bottom-nav'
import { RewardBanner } from '../components/reward-banner'
import type { Etudiant } from '@/shared/types/etudiant'
import { compressImage } from '@/lib/image'

// ── Helpers ─────────────────────────────────────────────────

function getInitials(prenom: string, nom: string): string {
  return `${prenom?.[0] ?? ''}${nom?.[0] ?? ''}`.toUpperCase()
}

function getPhotoUrl(etudiant: Etudiant): string | null {
  if (!etudiant.photoUrl) return null
  return etudiant.photoUrl
}

// ── Sub-components ──────────────────────────────────────────

function InfoRow({
  icon,
  label,
  value,
  editing,
  readOnly,
  placeholder,
  onEdit,
  onClose,
  onChange,
}: {
  icon: React.ReactNode
  label: string
  value: string
  editing?: boolean
  readOnly?: boolean
  placeholder?: string
  onEdit?: () => void
  onClose?: () => void
  onChange?: (v: string) => void
}) {
  return (
    <div className="flex items-center gap-3 border-b border-neutral-50 py-3 last:border-0">
      {icon}
      <div className="flex-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400">{label}</span>
        {editing ? (
          <div className="mt-0.5 flex items-center gap-2">
            <input
              autoFocus
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              className="h-8 flex-1 rounded-lg border border-primary/30 bg-primary/5 px-2 text-sm text-neutral-900 outline-none focus:border-primary"
            />
            <button onClick={onClose} className="text-neutral-400">
              <CheckCircle className="h-4 w-4 text-primary" />
            </button>
          </div>
        ) : (
          <div className="mt-0.5 flex items-center justify-between">
            <span className={cn('text-sm', value ? 'text-neutral-800' : 'text-neutral-300')}>
              {value || placeholder || '—'}
            </span>
            {!readOnly && (
              <button onClick={onEdit} className="text-neutral-300 active:text-primary">
                <Edit3 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function ConsentToggle({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between border-b border-neutral-50 py-3 last:border-0">
      <span className="text-sm text-neutral-700">{label}</span>
      <button
        onClick={() => onChange(!checked)}
        className={cn(
          'relative h-7 w-12 rounded-full transition-colors',
          checked ? 'bg-primary' : 'bg-neutral-200',
        )}
      >
        <div
          className={cn(
            'absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0.5',
          )}
        />
      </button>
    </div>
  )
}

// ── Main Page ───────────────────────────────────────────────

export function ProfileMobilePage() {
  const { user, logout } = useAuth()
  const { data: etudiant, isLoading } = useEtudiantByUserId(user?.id)

  if (isLoading || !etudiant || !user) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return <ProfileContent etudiant={etudiant} onLogout={logout} />
}

function ProfileContent({ etudiant, onLogout }: { etudiant: Etudiant; onLogout: () => void }) {
  const { t, i18n } = useTranslation()
  const { isDark, toggleDark } = useDarkMode()
  const today = useMemo(() => new Date(), [])
  const { data: agenda } = useAgendaComplet(etudiant.id, today)
  const [pushEnabled, setPushEnabled] = useState(false)
  const [pushLoading, setPushLoading] = useState(false)
  const pushSupported = isPushSupported()

  // Check current push subscription status on mount
  useEffect(() => {
    if (!pushSupported) return
    isPushSubscribed().then(setPushEnabled).catch(() => {})
  }, [pushSupported])

  const handleTogglePush = async () => {
    setPushLoading(true)
    try {
      if (pushEnabled) {
        await unsubscribeFromPush()
        setPushEnabled(false)
      } else {
        const success = await subscribeToPush()
        setPushEnabled(success)
      }
    } catch {
      // permission denied or error
    } finally {
      setPushLoading(false)
    }
  }

  const [editingField, setEditingField] = useState<string | null>(null)
  const [editValues, setEditValues] = useState({
    prenom: etudiant.prenom,
    nom: etudiant.nom,
    telephone: etudiant.telephone || '',
    nbMotsLangue: etudiant.nbMotsLangue,
    heureNotificationScan: etudiant.heureNotificationScan,
    consentementSilencieux: etudiant.consentementSilencieux,
    consentementConsultation: etudiant.consentementConsultation,
    consentementModificationPlanning: etudiant.consentementModificationPlanning,
  })
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [photoRemoved, setPhotoRemoved] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const updateMutation = useUpdateEtudiant()
  const [saved, setSaved] = useState(false)

  // Sync local state when etudiant data refreshes (after a save)
  useEffect(() => {
    setEditValues({
      prenom: etudiant.prenom,
      nom: etudiant.nom,
      telephone: etudiant.telephone || '',
      nbMotsLangue: etudiant.nbMotsLangue,
      heureNotificationScan: etudiant.heureNotificationScan,
      consentementSilencieux: etudiant.consentementSilencieux,
      consentementConsultation: etudiant.consentementConsultation,
      consentementModificationPlanning: etudiant.consentementModificationPlanning,
    })
  }, [etudiant])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const displayPhoto = photoRemoved ? null : (previewUrl || getPhotoUrl(etudiant))

  const hasChanges =
    editValues.prenom !== etudiant.prenom ||
    editValues.nom !== etudiant.nom ||
    editValues.telephone !== (etudiant.telephone || '') ||
    editValues.nbMotsLangue !== etudiant.nbMotsLangue ||
    editValues.heureNotificationScan !== etudiant.heureNotificationScan ||
    editValues.consentementSilencieux !== etudiant.consentementSilencieux ||
    editValues.consentementConsultation !== etudiant.consentementConsultation ||
    editValues.consentementModificationPlanning !== etudiant.consentementModificationPlanning ||
    photoFile !== null ||
    photoRemoved

  const handleSave = async () => {
    const formData = new FormData()
    formData.append(
      'etudiant',
      new Blob([JSON.stringify(editValues)], { type: 'application/json' }),
    )
    if (photoFile) formData.append('file', photoFile)
    if (photoRemoved) formData.append('removePhoto', new Blob(['true'], { type: 'application/json' }))
    try {
      await updateMutation.mutateAsync({ id: etudiant.id, formData })
      setSaved(true)
      setPhotoFile(null)
      setPhotoRemoved(false)
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }
      setTimeout(() => setSaved(false), 2000)
    } catch {
      // Error is handled by TanStack Query
    }
  }

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const compressed = await compressImage(file)
      setPhotoFile(compressed)
      setPhotoRemoved(false)
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      setPreviewUrl(URL.createObjectURL(compressed))
    }
    e.target.value = ''
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-gradient-to-b from-primary/8 via-primary/[0.02] to-white">
      {/* Hero section with avatar */}
      <div className="relative flex flex-col items-center pb-6 pt-4">
        {/* Avatar */}
        <div className="relative">
          <div
            className="h-28 w-28 overflow-hidden rounded-full border-4 border-white shadow-xl"
            onClick={() => fileRef.current?.click()}
          >
            {displayPhoto ? (
              <img src={displayPhoto} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary to-primary/60 text-3xl font-bold text-white">
                {getInitials(editValues.prenom, editValues.nom)}
              </div>
            )}
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            className="absolute bottom-0 right-0 flex h-9 w-9 items-center justify-center rounded-full border-3 border-white bg-primary text-white shadow-md"
          >
            <Camera className="h-4 w-4" />
          </button>
          {displayPhoto && (
            <button
              onClick={() => { setPhotoFile(null); setPhotoRemoved(true); if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null) } }}
              className="absolute top-0 right-0 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-red-500 text-white shadow-md"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoSelect} className="hidden" />
        </div>

        {/* Name */}
        <h1 className="mt-3 text-xl font-bold text-neutral-900">
          {editValues.prenom} {editValues.nom}
        </h1>
        <p className="text-sm text-neutral-400">{etudiant.email}</p>

        {/* Parcours badge */}
        {etudiant.parcours?.[0] && (
          <div className="mt-2 flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1">
            <GraduationCap className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold text-primary">
              {etudiant.parcours[0].niveau}
              {etudiant.parcours[0].etablissement ? ` — ${etudiant.parcours[0].etablissement}` : ''}
            </span>
          </div>
        )}
      </div>

      {/* Cards */}
      <div className="flex-1 space-y-3 px-4 pb-28">
        {/* Reward banner */}
        {agenda && <RewardBanner totalTachesTerminees={agenda.totalTachesTerminees} />}

        {/* Personal info card */}
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-neutral-400">
            {t('mobile.profile.personalInfo')}
          </h2>

          <InfoRow
            icon={<User className="h-4 w-4 text-primary/60" />}
            label={t('mobile.profile.firstName')}
            value={editValues.prenom}
            editing={editingField === 'prenom'}
            onEdit={() => setEditingField('prenom')}
            onClose={() => setEditingField(null)}
            onChange={(v) => setEditValues((p) => ({ ...p, prenom: v }))}
          />
          <InfoRow
            icon={<User className="h-4 w-4 text-primary/60" />}
            label={t('mobile.profile.lastName')}
            value={editValues.nom}
            editing={editingField === 'nom'}
            onEdit={() => setEditingField('nom')}
            onClose={() => setEditingField(null)}
            onChange={(v) => setEditValues((p) => ({ ...p, nom: v }))}
          />
          <InfoRow
            icon={<Mail className="h-4 w-4 text-primary/60" />}
            label={t('mobile.profile.email')}
            value={etudiant.email}
            readOnly
          />
          <InfoRow
            icon={<Phone className="h-4 w-4 text-primary/60" />}
            label={t('mobile.profile.phone')}
            value={editValues.telephone}
            editing={editingField === 'telephone'}
            onEdit={() => setEditingField('telephone')}
            onClose={() => setEditingField(null)}
            onChange={(v) => setEditValues((p) => ({ ...p, telephone: v }))}
            placeholder={t('mobile.profile.notProvided')}
          />
        </div>

        {/* Appearance card */}
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-neutral-400">
            {t('mobile.profile.appearance')}
          </h2>

          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              {isDark ? <Moon className="h-4 w-4 text-indigo-400" /> : <Sun className="h-4 w-4 text-amber-500" />}
              <span className="text-sm text-neutral-700">{t('mobile.profile.darkMode')}</span>
            </div>
            <button
              onClick={toggleDark}
              className={cn(
                'relative h-7 w-12 rounded-full transition-colors',
                isDark ? 'bg-primary' : 'bg-neutral-200',
              )}
            >
              <div
                className={cn(
                  'absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform',
                  isDark ? 'translate-x-5' : 'translate-x-0.5',
                )}
              />
            </button>
          </div>
        </div>

        {/* Language card */}
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-neutral-400">
            {t('mobile.profile.language')}
          </h2>

          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <Globe className="h-4 w-4 text-primary/60" />
              <span className="text-sm text-neutral-700">{t('mobile.profile.appLanguage')}</span>
            </div>
            <div className="flex gap-1">
              {([['fr', 'FR'], ['en', 'EN']] as const).map(([code, label]) => (
                <button
                  key={code}
                  onClick={() => {
                    i18n.changeLanguage(code)
                    localStorage.setItem('language', code)
                  }}
                  className={cn(
                    'rounded-lg px-3 py-1 text-xs font-bold transition-all',
                    i18n.language === code
                      ? 'bg-primary text-white shadow-sm'
                      : 'bg-neutral-100 text-neutral-500',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Notifications card */}
        {pushSupported && (
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-neutral-400">
              {t('mobile.profile.notifications')}
            </h2>

            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                {pushEnabled ? (
                  <Bell className="h-4 w-4 text-primary/60" />
                ) : (
                  <BellOff className="h-4 w-4 text-neutral-400" />
                )}
                <span className="text-sm text-neutral-700">{t('mobile.profile.pushNotifications')}</span>
              </div>
              <button
                onClick={handleTogglePush}
                disabled={pushLoading}
                className={cn(
                  'relative h-7 w-12 rounded-full transition-colors',
                  pushEnabled ? 'bg-primary' : 'bg-neutral-200',
                  pushLoading && 'opacity-50',
                )}
              >
                <div
                  className={cn(
                    'absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform',
                    pushEnabled ? 'translate-x-5' : 'translate-x-0.5',
                  )}
                />
              </button>
            </div>
            {Notification.permission === 'denied' && (
              <p className="mt-1 text-xs text-red-400">
                {t('mobile.profile.pushBlocked')}
              </p>
            )}

            {/* Heure notification SCAN */}
            <div className="flex items-center justify-between border-t border-neutral-50 py-3">
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-primary/60" />
                <span className="text-sm text-neutral-700">Heure notification SCAN</span>
              </div>
              <div className="flex items-center gap-1">
                <select
                  value={Math.floor(editValues.heureNotificationScan / 60)}
                  onChange={(e) => {
                    const minute = editValues.heureNotificationScan % 60
                    setEditValues((p) => ({ ...p, heureNotificationScan: Number(e.target.value) * 60 + minute }))
                  }}
                  className="h-8 w-12 rounded-lg border border-neutral-200 bg-neutral-50 px-1 text-center text-sm font-semibold text-neutral-800"
                >
                  {Array.from({ length: 17 }, (_, i) => i + 6).map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
                <span className="text-sm font-medium text-neutral-400">h</span>
                <select
                  value={editValues.heureNotificationScan % 60}
                  onChange={(e) => {
                    const hour = Math.floor(editValues.heureNotificationScan / 60)
                    setEditValues((p) => ({ ...p, heureNotificationScan: hour * 60 + Number(e.target.value) }))
                  }}
                  className="h-8 w-12 rounded-lg border border-neutral-200 bg-neutral-50 px-1 text-center text-sm font-semibold text-neutral-800"
                >
                  <option value={0}>00</option>
                  <option value={30}>30</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Consents card */}
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-neutral-400">
            {t('mobile.profile.consents')}
          </h2>

          <ConsentToggle
            label={t('mobile.profile.silentConsent')}
            checked={editValues.consentementSilencieux}
            onChange={(v) => setEditValues((p) => ({ ...p, consentementSilencieux: v }))}
          />
          <ConsentToggle
            label={t('mobile.profile.consultationConsent')}
            checked={editValues.consentementConsultation}
            onChange={(v) => setEditValues((p) => ({ ...p, consentementConsultation: v }))}
          />
          <ConsentToggle
            label={t('mobile.profile.planningModification')}
            checked={editValues.consentementModificationPlanning}
            onChange={(v) => setEditValues((p) => ({ ...p, consentementModificationPlanning: v }))}
          />
        </div>

        {/* Logout */}
        <button
          onClick={onLogout}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-red-50 py-4 text-sm font-semibold text-red-500 transition-colors active:bg-red-100"
        >
          <LogOut className="h-4 w-4" />
          {t('mobile.profile.logout')}
        </button>
      </div>

      {/* Floating save button */}
      {hasChanges && (
        <div className="fixed inset-x-4 bottom-20 z-50">
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 text-sm font-bold text-white shadow-xl shadow-primary/30 transition-all active:scale-[0.98] disabled:opacity-60"
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : saved ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {updateMutation.isPending ? t('mobile.profile.saving') : saved ? t('mobile.profile.saved') : t('mobile.profile.saveChanges')}
          </button>
        </div>
      )}

      <BottomNav />
    </div>
  )
}
